import React, { useEffect, useState } from 'react';
import { GetRequest, PostRequest } from 'components/common';

// Custom OneDrive file browser using Microsoft Graph API
// This uses our existing OAuth tokens instead of the OneDrive SDK's separate auth flow
const OneDrivePicker = ({ dataSourceId, onWorkbooksPicked, onCancel }) => {
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch available Excel files from OneDrive via our backend
  useEffect(() => {
    setLoading(true);
    GetRequest({
      url: `/quote-builder/data-sources/${dataSourceId}/browse-files`,
      callback: (data) => {
        setFiles(data || []);
        setLoading(false);
      },
      401: (response) => {
        if (response?.needs_reauth) {
          setError(t('quote_builder.microsoft_connection_expired'));
        } else {
          setError(t('quote_builder.authentication_failed'));
        }
        setLoading(false);
      },
      422: (response) => {
        setError(response?.error || 'Failed to load files from OneDrive');
        setLoading(false);
      }
    });
  }, [dataSourceId]);

  const toggleFileSelection = (file) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  };

  const handleAddSelected = () => {
    if (selectedFiles.length === 0) return;

    setSaving(true);
    const workbooks = selectedFiles.map(file => ({
      id: file.id,
      name: file.name,
      url: file.webUrl
    }));

    PostRequest({
      url: `/quote-builder/data-sources/${dataSourceId}/pick-workbooks`,
      data: { workbooks },
      callback: (result) => {
        setSaving(false);
        onWorkbooksPicked(result.all_workbooks || []);
      },
      401: (result) => {
        setSaving(false);
        if (result?.needs_reauth) {
          setError(t('quote_builder.microsoft_connection_expired'));
        } else {
          setError(t('quote_builder.authentication_failed'));
        }
      },
      422: (result) => {
        setSaving(false);
        setError(t('quote_builder.failed_to_add_workbooks', { error: result?.error || 'Unknown error' }));
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Popup overlay style
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999
  };

  const popupStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <div style={overlayStyle}>
      <div style={popupStyle}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '15px 20px',
          borderBottom: '1px solid #eee'
        }}>
          <strong style={{ fontSize: '16px' }}>
            <i className="fa fa-cloud" style={{ marginRight: '8px', color: '#0078d4' }}></i>
            Select Excel Files from OneDrive
          </strong>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={onCancel}
            style={{ padding: '0', fontSize: '18px', color: '#666' }}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px', color: '#0078d4' }}></i>
              <div style={{ marginTop: '10px' }}>Loading files from OneDrive...</div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger">
              <i className="fa fa-exclamation-circle"></i> {error}
            </div>
          )}

          {!loading && !error && files.length === 0 && (
            <div className="alert alert-info">
              No Excel files found in your OneDrive. Upload some .xlsx files to OneDrive first.
            </div>
          )}

          {!loading && !error && files.length > 0 && (
            <div style={{
              maxHeight: '350px',
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: '4px',
              backgroundColor: '#fff'
            }}>
              {files.map(file => {
                const isSelected = selectedFiles.some(f => f.id === file.id);
                return (
                  <div
                    key={file.id}
                    onClick={() => toggleFileSelection(file)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 15px',
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      style={{ marginRight: '12px' }}
                    />
                    <i className="fa fa-file-excel-o" style={{ color: '#217346', marginRight: '12px', fontSize: '20px' }}></i>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{ fontWeight: isSelected ? '600' : '400' }}>{file.name}</div>
                      <small style={{ color: '#888' }}>
                        {formatFileSize(file.size)}
                        {file.lastModifiedDateTime && ` • Modified ${formatDate(file.lastModifiedDateTime)}`}
                      </small>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!loading && !error && files.length > 0 && (
          <div style={{
            padding: '15px 20px',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#666' }}>
              {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
            </span>
            <div>
              <button
                type="button"
                className="btn btn-default"
                onClick={onCancel}
                style={{ marginRight: '10px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddSelected}
                disabled={selectedFiles.length === 0 || saving}
              >
                {saving ? (
                  <><i className="fa fa-spinner fa-spin"></i> Adding...</>
                ) : (
                  <>Add Selected ({selectedFiles.length})</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OneDrivePicker;
